// Connect your own Claude (MCP). Claude (claude.ai, Claude Desktop, Claude Code) registers itself with the server,
// sends the person's browser to the consent page (`#/connect?…`), and gets a revocable key in their own name, in one
// workspace, with at most their role. Claude Code can also use a personal key in a header. This file holds the typed
// API calls and the pure pieces: checking the consent link, where a Cancel may go, and the texts people copy.

import type { Api } from "../api.ts";
import { BRAND } from "../brand.ts";
import type { Department } from "../departments.ts";
import { DEPARTMENT_LABEL } from "../departments.ts";
import { ROLE_NAME } from "../invite.ts";
import type { Role } from "../types.ts";

export { ROLE_NAME };

// ── the API ────────────────────────────────────────────────────────────────────

/** A registered OAuth client (Claude). Its name is chosen by the client itself, so it is shown, never trusted. */
export interface OAuthClient {
  client_id: string;
  client_name: string;
  redirect_uris: string[];
}

export type ConnectionSource = "oauth" | "personal" | "manual";

/** One of your own Claude connections or personal keys in a workspace (the key itself is never listed). */
export interface Connection {
  id: string;
  name: string | null;
  prefix: string;
  role: Role;
  source: ConnectionSource;
  client_name: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** A new personal key: the only time `key` is ever sent. */
export type NewPersonalKey = Connection & { key: string };

export interface ApproveBody {
  clientId: string;
  redirectUri: string;
  state?: string;
  codeChallenge: string;
  codeChallengeMethod: "S256";
  role?: Role;
}

export function connectApi(api: Api) {
  return {
    client: (id: string) => api.request<OAuthClient>("GET", `/oauth/clients/${encodeURIComponent(id)}`),
    /** Allow the client into workspace `ws` (the workspace is in the path, the person is the signed-in one). */
    approve: (ws: string, body: ApproveBody) =>
      api.request<{ redirect_to: string }>("POST", `/workspaces/${ws}/oauth/approve`, { body }),
    connections: (ws: string) =>
      api.request<{ items: Connection[] }>("GET", `/workspaces/${ws}/connections`).then((r) => r.items),
    createKey: (ws: string, name: string) =>
      api.request<NewPersonalKey>("POST", `/workspaces/${ws}/connections`, { body: { name } }),
    revoke: (ws: string, id: string) =>
      api.request<Connection>("POST", `/workspaces/${ws}/connections/${id}/revoke`, { body: {} }),
  };
}

export const activeConnections = (items: readonly Connection[]): Connection[] =>
  items.filter((c) => !c.revoked_at);

/** The "copy it now" panel after a connection was revoked: gone when it was the key shown, else untouched. */
export function keyAfterRevoke(shown: NewPersonalKey | null, revokedId: string): NewPersonalKey | null {
  return shown && shown.id === revokedId ? null : shown;
}

/** What a connection is called in the list: its own name, else the app that connected, else its kind. */
export function connectionName(c: Pick<Connection, "name" | "client_name" | "source">): string {
  const n = c.name?.trim() || c.client_name?.trim();
  if (n) return n;
  return c.source === "personal" ? "Personal key" : c.source === "oauth" ? "Claude" : "API key";
}

/** "Director · connected 2 d ago · used 3 min ago" (`ago` formats a time, e.g. timeAgo from ui/app.ts). */
export function connectionFacts(c: Connection, ago: (iso: string) => string): string {
  return [
    ROLE_NAME[c.role] ?? c.role,
    c.source === "personal" ? `key made ${ago(c.created_at)}` : `connected ${ago(c.created_at)}`,
    c.last_used_at ? `used ${ago(c.last_used_at)}` : "not used yet",
  ].join(" · ");
}

// ── roles, in plain words ──────────────────────────────────────────────────────

export const ROLE_ORDER: readonly Role[] = ["owner", "producer", "director", "maker", "reviewer", "viewer"];

/** What Claude can do with a role, said to a filmmaker. */
export const CLAUDE_CAN: Record<Role, string> = {
  owner: "everything in the workspace, including members, access and costs",
  producer: "run briefs, crew and costs, build shots, comment and sign off",
  director: "build shots, render takes, pick takes, comment and sign off",
  maker: "render and upload takes, and comment",
  reviewer: "read the productions and comment",
  viewer: "read the productions, and change nothing",
};

/** Your role and every role below it: the roles you can give Claude (owner first). */
export function rolesUpTo(mine: Role): Role[] {
  const i = ROLE_ORDER.indexOf(mine);
  return i < 0 ? ["viewer"] : ROLE_ORDER.slice(i);
}

/** "Claude can do what you can do: …" for your own role; "Claude can only …" for a lower one. */
export function claudeCanLine(chosen: Role, mine: Role): string {
  return chosen === mine
    ? `Claude can do what you can do: ${CLAUDE_CAN[chosen]}.`
    : `Claude can do less than you: ${CLAUDE_CAN[chosen]}.`;
}

// ── the consent link ───────────────────────────────────────────────────────────

/** The query of `#/connect?…`, as the server's /v1/oauth/authorize hands it over. */
export interface ConnectParams {
  responseType: string | null;
  clientId: string | null;
  redirectUri: string | null;
  state: string | null;
  codeChallenge: string | null;
  codeChallengeMethod: string | null;
  scope: string | null;
}

export interface ConnectCheck {
  params: ConnectParams;
  /** what is wrong with the link, in plain words; empty when Allow may be offered */
  problems: string[];
}

const CHALLENGE = /^[A-Za-z0-9_-]{43}$/;

/** Read and check the consent link. Allow is offered only when `problems` is empty. */
export function checkConnectQuery(query: string): ConnectCheck {
  const q = new URLSearchParams(query.replace(/^\?/, ""));
  const get = (k: string) => {
    const v = q.get(k);
    return v === null || v.trim() === "" ? null : v;
  };
  const params: ConnectParams = {
    responseType: get("response_type"),
    clientId: get("client_id"),
    redirectUri: get("redirect_uri"),
    state: get("state"),
    codeChallenge: get("code_challenge"),
    codeChallengeMethod: get("code_challenge_method"),
    scope: get("scope"),
  };
  const problems: string[] = [];
  if (!params.clientId) problems.push("The link does not say which app is asking (client_id is missing).");
  if (!params.redirectUri)
    problems.push("The link does not say where to send you back (redirect_uri is missing).");
  else if (!isAllowedRedirect(params.redirectUri))
    problems.push("The address to send you back to is not a safe one (it must be https, or this computer).");
  if (!params.responseType)
    problems.push("The link does not say what it asks for (response_type is missing).");
  else if (params.responseType !== "code")
    problems.push(`The link asks for “${clip(params.responseType)}”; ${BRAND.name} only hands out a code.`);
  if (!params.codeChallenge)
    problems.push("The link has no code challenge, so the connection could not be checked (code_challenge).");
  else if (!CHALLENGE.test(params.codeChallenge))
    problems.push("The code challenge in the link is not a valid S256 challenge.");
  if (!params.codeChallengeMethod)
    problems.push("The link does not say how the code is checked (code_challenge_method is missing).");
  else if (params.codeChallengeMethod !== "S256")
    problems.push(
      `The link checks the code with “${clip(params.codeChallengeMethod)}”; ${BRAND.name} only accepts S256.`,
    );
  return { params, problems };
}

const clip = (s: string, n = 40) => (s.length > n ? `${s.slice(0, n)}…` : s);

/** https anywhere, or http on this computer (Claude Code listens on localhost); no fragment, no credentials. */
export function isAllowedRedirect(uri: string): boolean {
  let u: URL;
  try {
    u = new URL(uri);
  } catch {
    return false;
  }
  if (u.hash || u.username || u.password) return false;
  if (u.protocol === "https:") return true;
  return u.protocol === "http:" && isLoopback(u.hostname);
}

const isLoopback = (host: string) => host === "localhost" || host === "127.0.0.1" || host === "[::1]";

/** Where Allow or Cancel sends you, as a person can check it: the host, and a word when it is this computer. */
export function returnHost(uri: string): { host: string; note: string | null } {
  try {
    const u = new URL(uri);
    if (isLoopback(u.hostname)) return { host: u.host, note: "an app on this computer, such as Claude Code" };
    return { host: u.host, note: null };
  } catch {
    return { host: uri, note: null };
  }
}

/** Is the redirect URI one the client registered (exact match, as the server checks it)? */
export const isRegistered = (
  client: Pick<OAuthClient, "redirect_uris"> | null,
  uri: string | null,
): boolean => !!client && !!uri && client.redirect_uris.includes(uri);

/**
 * Where Cancel sends the browser: `redirect_uri?error=access_denied&state=…`, but only to an address the client
 * registered (else null: show an error and stay here, so a forged link cannot bounce anyone anywhere).
 */
export function cancelUrl(
  params: ConnectParams,
  client: Pick<OAuthClient, "redirect_uris"> | null,
): string | null {
  const uri = params.redirectUri;
  if (!uri || !isRegistered(client, uri) || !isAllowedRedirect(uri)) return null;
  const u = new URL(uri);
  u.searchParams.set("error", "access_denied");
  if (params.state) u.searchParams.set("state", params.state);
  return u.toString();
}

/** Does the server's `redirect_to` go back to the registered address it was asked for (same origin and path)? */
export function isSafeReturn(redirectTo: string, redirectUri: string): boolean {
  try {
    const a = new URL(redirectTo);
    const b = new URL(redirectUri);
    return a.origin === b.origin && a.pathname === b.pathname && isAllowedRedirect(redirectUri);
  } catch {
    return false;
  }
}

/** The heading. The client names itself, so a name that is not Claude's is quoted rather than taken for Claude. */
export function connectHeading(clientName: string | null | undefined): string {
  const n = clientName?.trim() ?? "";
  if (!n || /\bclaude\b/i.test(n)) return `Claude wants to work in ${BRAND.name} as you`;
  return `“${clip(n, 60)}” wants to work in ${BRAND.name} as you`;
}

/** Why approve failed, in plain words. */
export function approveErrorText(e: unknown): string {
  const { status, code, message } = (e && typeof e === "object" ? e : {}) as {
    status?: number;
    code?: string;
    message?: string;
  };
  if (code === "not_a_person" || code === "api_key_not_allowed")
    return "Connecting Claude needs a sign-in with your email. This browser is signed in with an API key: sign out and sign in with your email, or use a personal key from Settings → Claude.";
  if (code === "invalid_client")
    return `${BRAND.name} does not know this app. Start connecting again from Claude.`;
  if (code === "invalid_redirect_uri")
    return "The address to send you back to was not registered by this app. Start connecting again from Claude.";
  if (code === "invalid_request")
    return message ? `The link is not right: ${message}.` : "The link is not right.";
  if (status === 404 || status === 403)
    return "You are not a member of that workspace, or your role there does not allow it.";
  return message || "Claude could not be connected. Try again.";
}

// ── sign-in round trip ─────────────────────────────────────────────────────────

const PENDING_KEY = "filmsquare.connect";
/** A consent link opened while signed out stays good this long (Claude waits for the code meanwhile). */
export const PENDING_TTL_MS = 30 * 60_000;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** The query of a `#/connect?…` address; null for any other address. */
export function connectQueryOfHash(hash: string): string | null {
  const m = /^#\/?connect(?:\?(.*))?$/s.exec(hash);
  return m ? (m[1] ?? "") : null;
}

/**
 * The consent link survives a sign-in: an email link comes back to the bare origin, so the query is kept in this
 * browser (localStorage: the sign-in link may open in another tab) and the app returns to it after sign-in.
 */
export function createPendingConnect(storage: Store | null) {
  return {
    save(query: string, now = Date.now()) {
      try {
        storage?.setItem(PENDING_KEY, JSON.stringify({ query, at: now }));
      } catch {
        // storage blocked: the link only survives in this address bar
      }
    },
    /** The saved query when it is still fresh; it is forgotten either way. */
    take(now = Date.now()): string | null {
      try {
        const raw = storage?.getItem(PENDING_KEY);
        if (!raw) return null;
        storage?.removeItem(PENDING_KEY);
        const v = JSON.parse(raw) as { query?: unknown; at?: unknown };
        if (typeof v.query !== "string" || typeof v.at !== "number") return null;
        return now - v.at <= PENDING_TTL_MS && now >= v.at ? v.query : null;
      } catch {
        return null;
      }
    },
    has(): boolean {
      try {
        return !!storage?.getItem(PENDING_KEY);
      } catch {
        return false;
      }
    },
  };
}

/** localStorage, looked up on first use (so importing this file touches no browser global). */
function localStore(): Store | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

const lazyLocal: Store = {
  getItem: (k) => localStore()?.getItem(k) ?? null,
  setItem: (k, v) => localStore()?.setItem(k, v),
  removeItem: (k) => localStore()?.removeItem(k),
};

export const pendingConnect = createPendingConnect(lazyLocal);

// ── the texts people copy ──────────────────────────────────────────────────────

/** The MCP address: next to the API (`/v1/mcp`), on the API's own origin when the app talks to another one. */
export function mcpUrl(apiBase: string, origin: string): string {
  let end = apiBase.length;
  while (end > 0 && apiBase[end - 1] === "/") end--;
  return new URL(`${apiBase.slice(0, end)}/mcp`, origin).toString();
}

export const claudeCodeCommand = (url: string): string =>
  `claude mcp add --transport http ${BRAND.id} ${url}`;

export const claudeCodeKeyCommand = (url: string, key: string): string =>
  `${claudeCodeCommand(url)} --header "Authorization: Bearer ${key}"`;

/** What people can ask once Claude is connected; names a real production when there is one. */
export function examplePrompts(production?: string | null): string[] {
  const p = production?.trim() || "<production>";
  return [
    `What is waiting for me in ${BRAND.name}?`,
    `Go through the open feedback on ${p} and handle it`,
    `Break down the script of ${p} · Episode 1`,
  ];
}

export interface EpisodeRef {
  productionId: string;
  production: string;
  episodeId: string;
  number: number;
}

const episodeLabel = (e: EpisodeRef) => `${e.production} · Episode ${e.number}`;

export const breakDownPrompt = (e: EpisodeRef): string =>
  `Use the ${BRAND.name} break_down_episode prompt for episode ${e.episodeId} (${episodeLabel(e)}).`;

/**
 * Bring scripts and writing documents in: the bring_in_writing prompt, so a script lands in the episode's Writing
 * step (with its breakdown) and a synopsis or character description lands as a sheet, never as a loose upload.
 */
export const bringInWritingPrompt = (e: EpisodeRef): string =>
  `Use the ${BRAND.name} bring_in_writing prompt for production ${e.productionId}, episode ${e.episodeId} (${episodeLabel(e)}): bring my script and writing documents in; the script goes in with submit_breakdown, the rest as Writing sheets, never through upload_file.`;

export const processFeedbackPrompt = (e: EpisodeRef): string =>
  `Use the ${BRAND.name} process_feedback prompt for production ${e.productionId}, episode ${e.episodeId} (${episodeLabel(e)}).`;

export const workOnStepPrompt = (e: EpisodeRef, dept: Department): string =>
  `Use the ${BRAND.name} work_on_step prompt for episode ${e.episodeId}, department ${dept} (${episodeLabel(e)} · ${DEPARTMENT_LABEL[dept]}).`;

/** Hand over what your own pipeline made: the deliver_to_filmsquare prompt, for the step you are on. */
export const deliverWorkPrompt = (e: EpisodeRef, dept: Department): string =>
  `Use the ${BRAND.name} deliver_to_filmsquare prompt for production ${e.productionId}, episode ${e.episodeId} (${episodeLabel(e)}): deliver what my pipeline made for ${DEPARTMENT_LABEL[dept]} as sheets.`;

/**
 * The board's "Work on this with Claude" prompts, in the order shown. `shown` is the prompt in plain words, with
 * names (what a person reads); `text` is what is copied or opened, carrying the ids your Claude needs. Writing
 * starts with bringing the script and writing documents in.
 */
export function boardPrompts(
  e: EpisodeRef,
  dept: Department,
): { id: string; title: string; shown: string; text: string }[] {
  const where = episodeLabel(e);
  const step = DEPARTMENT_LABEL[dept];
  const writing =
    dept === "writing"
      ? [
          {
            id: "writing",
            title: "Bring in your script and writing",
            shown: `Bring my script, synopsis and character descriptions into ${where}.`,
            text: bringInWritingPrompt(e),
          },
        ]
      : [];
  return [
    ...writing,
    {
      id: "feedback",
      title: "Handle the open feedback",
      shown: `Go through the open feedback on ${where} and handle it.`,
      text: processFeedbackPrompt(e),
    },
    {
      id: "step",
      title: `Work on ${step}`,
      shown: `Work on ${step} for ${where}.`,
      text: workOnStepPrompt(e, dept),
    },
    {
      id: "deliver",
      title: "Deliver your own work as sheets",
      shown: `Deliver what my own pipeline made for ${step} on ${where}, as sheets.`,
      text: deliverWorkPrompt(e, dept),
    },
    {
      id: "breakdown",
      title: "Break down the script",
      shown: `Break down the script of ${where}.`,
      text: breakDownPrompt(e),
    },
  ];
}

/**
 * claude.ai with the prompt filled in. Filmsquare has no claude:// deep link, so the web app is the way in; the
 * prompt is the only thing in the address (no names of people, no keys).
 */
export const claudeUrl = (prompt: string): string => `https://claude.ai/new?q=${encodeURIComponent(prompt)}`;
